import type { Command } from 'commander';
import { LOG_LEVEL } from '@gitlab-org/logging';
import { getAppName } from '@gitlab-org/tui';
import { registerOptionsOnCommand } from '../option_def';
import { sharedOptionDefs } from '../shared_option_defs';
import {
  tuiCommandOptionDefs,
  runCommandOptionDefs,
  exportCommandOptionDefs,
} from '../command_option_defs';
import {
  gitlabSharedOptionDefs,
  gitlabRunOptionDefs,
  anthropicOptionDefs,
} from '../backend_option_defs';
import { GitLabBackendConfigAdapter } from '../backend/gitlab/gitlab_backend_config_adapter';
import type { ExitHandler } from '../utils/exit';
import { installSourceMapStacks } from '../utils/source_map_stacks';
import { helpHint } from '../utils/help_hint';
import type { DuoCommand, OptionGroup } from './duo_command';
import { TuiCommand, gitlabTuiExamples, gitlabTuiSynopsis } from './tui/tui_command';
import { RunCommand, gitlabRunExamples, gitlabRunSynopsis } from './run/run_command';
import {
  ExportCommand,
  gitlabExportExamples,
  gitlabExportNotes,
  gitlabExportSynopsis,
} from './export/export_command';
import { DirectGroupCommand, directGroupNotes } from './direct/direct_group_command';
import { VersionCommand } from './version_command';
import { HelpCommand } from './help_command';
import { ConfigGroupCommand } from './config/config_group_command';
import { LogGroupCommand } from './log/log_group_command';
import { PluginGroupCommand } from './plugin/plugin_group_command';
import { AcpCommand } from './acp/acp_command';

const rootSynopsis =
  'GitLab Duo CLI ships an interactive terminal UI (TUI) ' +
  'plus auxiliary subcommands for headless workflow runs, configuration, and log management.';

export interface RootCommandOptions {
  version: string;
  exitHandler: ExitHandler;
  /** Reference back to the program — used by `HelpCommand` to print full help. */
  program: Command;
}

/**
 * Root of the command tree (`duo`). Carries metadata for the top-level
 * heading and global options, and owns the list of top-level subcommands.
 *
 * `register(program)` wires program-level commander config (name, version,
 * description, global options, debug pre-action hook). The
 * {@link CommandBootstrapper} should then `attach(program, root.children)`
 * to wire the rest of the tree.
 *
 * The doc generator walks the same instance: it renders root metadata plus
 * `children` recursively.
 */
export class RootCommand implements DuoCommand {
  readonly name = 'duo';

  readonly description =
    'GitLab Duo for your command line.' +
    '\n\nFor detailed feature documentation, see [GitLab Duo CLI](https://docs.gitlab.com/user/gitlab_duo_cli/).' +
    '\n\n> **Note:** If you use the GitLab Duo CLI through the GitLab CLI (`glab`), replace `duo` with `glab duo cli` in all examples in this reference.';

  readonly synopsis = rootSynopsis;

  readonly optionGroups: OptionGroup[] = [{ title: 'Global options', options: sharedOptionDefs }];

  readonly notes =
    '`GITLAB_BIN_DIR` has no flag: it sets the directory that bundled executables are extracted ' +
    'into, instead of the OS temporary directory, and is meant to be set fleet-wide by ' +
    'administrators rather than per user. See ' +
    '[Redirect bundled executables away from the temporary directory](../../../README.md#redirect-bundled-executables-away-from-the-temporary-directory).';

  readonly children: DuoCommand[];

  readonly #version: string;

  constructor(options: RootCommandOptions) {
    const { version, exitHandler, program } = options;
    this.#version = version;
    const gitlabAdapter = new GitLabBackendConfigAdapter();
    const gitlabSharedOptionDefsArray = Object.values(gitlabSharedOptionDefs);

    this.children = [
      new TuiCommand({
        adapter: gitlabAdapter,
        version,
        exitHandler,
        synopsis: gitlabTuiSynopsis,
        examples: gitlabTuiExamples,
        optionGroups: [
          {
            title: '`tui` options',
            options: [...Object.values(tuiCommandOptionDefs), ...gitlabSharedOptionDefsArray],
          },
        ],
      }),
      new RunCommand({
        adapter: gitlabAdapter,
        version,
        exitHandler,
        examples: gitlabRunExamples,
        synopsis: gitlabRunSynopsis,
        optionGroups: [
          {
            title: '`run` options',
            options: [
              ...Object.values(runCommandOptionDefs),
              ...gitlabSharedOptionDefsArray,
              ...Object.values(gitlabRunOptionDefs),
            ],
          },
        ],
      }),
      new ExportCommand({
        adapter: gitlabAdapter,
        version,
        exitHandler,
        examples: gitlabExportExamples,
        synopsis: gitlabExportSynopsis,
        notes: gitlabExportNotes,
        optionGroups: [
          {
            title: '`export` options',
            options: [...Object.values(exportCommandOptionDefs), ...gitlabSharedOptionDefsArray],
          },
        ],
      }),
      new DirectGroupCommand({
        version,
        exitHandler,
        optionGroups: [{ title: 'Anthropic backend options', options: anthropicOptionDefs }],
        notes: directGroupNotes,
      }),
      new VersionCommand(version),
      new HelpCommand(program),
      new ConfigGroupCommand(exitHandler),
      new LogGroupCommand(),
      new PluginGroupCommand(exitHandler),
      // ACP command is gated behind GITLAB_DUO_ACP=true until the feature is
      // stable enough for general availability. When the flag is absent the
      // command is not registered at all (not merely hidden), so it cannot be
      // invoked even by users who know the name.
      ...(process.env.GITLAB_DUO_ACP === 'true'
        ? [
            new AcpCommand({
              adapter: gitlabAdapter,
              version,
              exitHandler,
            }),
          ]
        : []),
    ];
  }

  register(program: Command): void {
    program
      .name(getAppName())
      .description(this.description)
      // The version flag is wired here rather than via VersionCommand because
      // commander treats `-v / --version` specially as a top-level flag.
      .version(this.#version, '-v, --version', 'Display version number')
      // `Command#showHelpAfterError` is copied onto every child at `.command()`
      // time (see commander's `copyInheritedSettings`), so setting it here
      // once cascades to every subcommand group (`plugin`, `config`, `log`,
      // the hidden `direct` group, ...) — any native commander error there
      // (unknown command, excess arguments, unknown option, ...) gets the
      // same hint `TuiCommand` throws manually for the top-level case.
      .showHelpAfterError(helpHint());

    registerOptionsOnCommand(program, sharedOptionDefs);

    program.hook('preAction', (thisCommand) => {
      const opts = thisCommand.optsWithGlobals();
      if (opts.enableGlobalSkills !== undefined) {
        // eslint-disable-next-line no-console
        console.warn(
          'Warning: --enable-global-skills (GITLAB_ENABLE_GLOBAL_SKILLS) is deprecated and has no effect. Global agent skills are always enabled. The flag will be removed in a future version.',
        );
      }
      if (opts.logLevel === LOG_LEVEL.DEBUG) {
        installSourceMapStacks();
      }
    });
  }
}
